import type { ReactNode, SVGProps } from 'react';
import { useI18n } from './i18n.js';

/** The Mynstra wordmark with "BIM": the platform's lockup. The logo is drawn by a CSS mask, so it takes the text colour. */
export function Lockup({ large = false }: { large?: boolean }) {
  const { t } = useI18n();
  return (
    <span className={`lockup${large ? ' large' : ''}`} role="img" aria-label={t('appName')}>
      <span className="wordmark" />
      <span className="lockup-bim">BIM</span>
    </span>
  );
}

function Svg({ children, ...rest }: SVGProps<SVGSVGElement> & { children: ReactNode }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const Icon = {
  Plus: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  ),
  /** A building in elevation: the project's thumbnail until a model still exists. */
  Building: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <path d="M4 21V9l6-4v16M10 21V3l10 4v14M3 21h18" />
      <path d="M13.5 8.5h3M13.5 12h3M13.5 15.5h3M6.5 12h1M6.5 15.5h1" />
    </Svg>
  ),
  User: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
    </Svg>
  ),
  /** A frozen state (a revision). */
  Frozen: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M8 12h8M12 8v8" />
    </Svg>
  ),
  Inbox: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <path d="M3 13h5l1.5 3h5L16 13h5" />
      <path d="M5 5h14l2 8v6H3v-6z" />
    </Svg>
  ),
  List: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />
    </Svg>
  ),
  Map: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2z" />
      <path d="M9 4v14M15 6v14" />
    </Svg>
  ),
  Sort: ({ dir, ...p }: SVGProps<SVGSVGElement> & { dir: 1 | -1 }) => (
    <Svg width={12} height={12} {...p}>
      <path d={dir === 1 ? 'm6 9 6 6 6-6' : 'm6 15 6-6 6 6'} />
    </Svg>
  ),
  /** An axonometric box: the model. */
  Model: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <path d="M12 2.8 20.5 7.5v9L12 21.2 3.5 16.5v-9z" />
      <path d="M3.5 7.5 12 12.2l8.5-4.7M12 12.2v9" />
    </Svg>
  ),
  Download: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <path d="M12 4v11m0 0-4.5-4.5M12 15l4.5-4.5M5 19.5h14" />
    </Svg>
  ),
  File: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <path d="M6.5 3h7.5l4.5 4.5v13.5h-12z" />
      <path d="M14 3v4.5h4.5" />
    </Svg>
  ),
  Folder: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <path d="M3.5 6.5h6l2 2h9v10h-17z" />
    </Svg>
  ),
  Chevron: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <path d="m9.5 6 6 6-6 6" />
    </Svg>
  ),
  Search: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4 4" />
    </Svg>
  ),
  Copy: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <rect x="8.5" y="8.5" width="11" height="11" rx="1.5" />
      <path d="M15.5 8.5v-3h-11v11h3" />
    </Svg>
  ),
  /** A browser window with the model in it. */
  Viewer: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <rect x="3" y="4.5" width="18" height="15" rx="1.5" />
      <path d="M3 8.5h18" />
      <path d="m12 10.8 3.6 2v4l-3.6 2-3.6-2v-4z" />
    </Svg>
  ),
  /** A drawing sheet: a plan in a frame with a title block. */
  Sheet: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <rect x="3" y="4" width="18" height="16" rx="1" />
      <path d="M6.5 7.5h7v8h-7zM10 7.5v4.5M13.5 16.5h4.5" />
    </Svg>
  ),
  /** A floor area with its dimension lines. */
  Area: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <rect x="7" y="3.5" width="13.5" height="13.5" />
      <path d="M3.5 3.5v13.5M2.5 3.5h2M2.5 17h2M7 20.5h13.5M7 19.5v2M20.5 19.5v2" />
    </Svg>
  ),
  More: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <circle cx="5" cy="12" r="1.2" fill="currentColor" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
      <circle cx="19" cy="12" r="1.2" fill="currentColor" />
    </Svg>
  ),
  Close: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Svg>
  ),
  /** A paper plane: deliver. */
  Send: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <path d="M3.5 11.5 20.5 4l-5.5 16-3.5-6.5z" />
      <path d="m11.5 13.5 9-9.5" />
    </Svg>
  ),
  Lock: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <rect x="5" y="11" width="14" height="9" rx="1.5" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </Svg>
  ),
  Check: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </Svg>
  ),
  /** A page with bars: a report. */
  Report: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <rect x="4" y="4" width="16" height="16" rx="1.5" />
      <path d="M8.5 16v-3.5M12 16V8.5M15.5 16v-5.5" />
    </Svg>
  ),
  Undo: (p: SVGProps<SVGSVGElement>) => (
    <Svg {...p}>
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
    </Svg>
  ),
  Microsoft: () => (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  ),
};

/**
 * The structural grid of a plan drawing, behind the signed-out pages: dash-dot axis lines with lettered and numbered
 * grid bubbles, as on the drawings and in the model viewer.
 */
export function GridBackdrop() {
  const step = 200;
  const cols = 'ABCDEFGHIJKL'.split('');
  const rows = [1, 2, 3, 4, 5, 6, 7, 8];
  const r = 13;
  return (
    <svg className="gridlines" width="2560" height="1700" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="26 6 3 6">
        {cols.map((_, i) => (
          <line key={`v${i}`} x1={160 + i * step} y1={40 + r} x2={160 + i * step} y2={1700} />
        ))}
        {rows.map((_, j) => (
          <line key={`h${j}`} x1={40 + r} y1={170 + j * step} x2={2560} y2={170 + j * step} />
        ))}
      </g>
      <g className="gridlines-bubbles">
        {cols.map((c, i) => (
          <g key={c} transform={`translate(${160 + i * step} 40)`}>
            <circle r={r} />
            <text dy="4.2">{c}</text>
          </g>
        ))}
        {rows.map((n, j) => (
          <g key={n} transform={`translate(40 ${170 + j * step})`}>
            <circle r={r} />
            <text dy="4.2">{n}</text>
          </g>
        ))}
      </g>
    </svg>
  );
}
